import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import {
    ChangeDetectorRef,
    DestroyRef,
    Directive,
    ElementRef,
    HostBinding,
    ɵIMAGE_CONFIG as IMAGE_CONFIG,
    ɵIMAGE_CONFIG_DEFAULTS as IMAGE_CONFIG_DEFAULTS,
    ɵImageConfig as ImageConfig,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    booleanAttribute,
    inject,
    numberAttribute
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@df/core/app';
import { NCore } from '@df/core/df-core.interfaces';
import { Acl } from '@df/core/service/acl';
import { Client } from '@df/core/service/client';
import { Url } from '@df/core/service/url';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { IResizeOptions } from '@df/ui/common/resize.service';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { take } from 'rxjs/internal/operators/take';
import { NDfModuleImage } from './df-module-image.interface';
import { PreloadLinkCreator } from './preload-link-creator';

/**
 * RegExpr to determine whether a src in a srcset is using width descriptors.
 * Should match something like: "100w, 200w".
 */
const VALID_WIDTH_DESCRIPTOR_SRCSET = /^((\s*\d+w\s*(,|$)){1,})$/;

/**
 * Srcset values with a density descriptor higher than this value will actively
 * throw an error. Such densities are not permitted as they cause image sizes
 * to be unreasonably large and slow down LCP.
 */
export const ABSOLUTE_SRCSET_DENSITY_CAP = 3;

/**
 * Used only in error message text to communicate best practices, as we will
 * only throw based on the slightly more conservative ABSOLUTE_SRCSET_DENSITY_CAP.
 */
export const RECOMMENDED_SRCSET_DENSITY_CAP = 2;

/**
 * Used in generating automatic density-based srcsets
 */
const DENSITY_SRCSET_MULTIPLIERS = [1, 2];

/**
 * Used to determine which breakpoints to use on full-width images
 */
const VIEWPORT_BREAKPOINT_CUTOFF = 414;

/**
 * Used to limit automatic srcset generation of very large sources for
 * fixed-size images. In pixels.
 */
const FIXED_SRCSET_WIDTH_LIMIT = 1920;
const FIXED_SRCSET_HEIGHT_LIMIT = 1080;

/**
 * Default blur radius of the CSS filter used on placeholder images, in pixels
 */
export const PLACEHOLDER_BLUR_AMOUNT = 15;

/**
 * Debounce time for resize events to prevent excessive updates
 */
const RESIZE_DEBOUNCE_TIME = 100;

/**
 * Config options used in rendering placeholder images.
 */
export interface ImagePlaceholderConfig {
    blur?: boolean;
}

/**
 * Noop image loader that does no transformation to the original src and just returns it as is.
 * This loader is used as a default one if more specific logic is not provided in an app config.
 */
export const noopImageLoader = (config: ImageLoaderConfig) => config.src;

@Directive({
    standalone: true,
    selector: 'img[dfImage]',
    host: {
        '[style.aspect-ratio]': 'ratio',
        '[style.position]': 'fill ? "absolute" : null',
        '[style.width]': 'fill ? "100%" : null',
        '[style.height]': 'fill ? "100%" : null',
        '[style.inset]': 'fill ? "0" : null',
        '[style.object-fit]': 'ratio === "auto" || (fill && ratio) ? "cover" : null',
        '[style.background-size]': 'placeholder ? "cover" : null',
        '[style.background-color]': 'shouldColorPlaceholder',
        '[style.background-position]': 'placeholder ? "50% 50%" : null',
        '[style.background-repeat]': 'placeholder ? "no-repeat" : null',
        '[style.background-image]': 'placeholder ? generatePlaceholder(placeholder) : null',
        '[style.filter]': 'shouldBlurPlaceholder(placeholderConfig)',
        '[style.transition]': 'zoom ? "scale 0.25ms ease-in-out" : null',
        '(mouseenter)': 'handleMouseEnter()',
        '(mouseleave)': 'handleMouseLeave()',
        '(error)': 'handleError()',
        '(load)': 'handleLoad()',
        '[class.zoom]': 'zoom'
    }
})
export class DfImageDirective implements OnInit, OnChanges, OnDestroy {
    /**
     * Name of the source image.
     * Image name will be processed by the image loader and the final URL will be applied as the `src`
     * property of the image.
     */
    @Input({
        required: true,
        transform: imageAttribute
    })
    dfImage!: NDfModuleImage.IImage;

    /**
     * A comma separated list of width or density descriptors.
     * The image name will be taken from `dfImage` and combined with the list of width or density
     * descriptors to generate the final `srcset` property of the image.
     *
     * @usageNotes
     * ```html
     * <img dfImage="hello.jpg" dfSrcset="100w, 200w" />  =>
     * <img src="path/hello.jpg" srcset="path/hello.jpg?w=100 100w, path/hello.jpg?w=200 200w" />
     * ```
     */
    @Input()
    dfSrcset!: string;

    /**
     * Image ratio, for example 3/4 (width/height) or 0.75, or data from CMS `ratio-3-4`
     *
     * If `auto` is provided, the ratio will be calculated based on the image's intrinsic width and height.
     *
     * @usageNotes
     * ```html
     * <img dfImage="hello.jpg" ratio="3/4" />
     * <img dfImage="hello.jpg" ratio="0.75'" />
     * <img dfImage="hello.jpg" ratio="'ratio-3-4'" />
     * ```
     */
    @Input({ transform: ratioAttribute })
    ratio?: number | 'auto';

    /**
     * The base `sizes` attribute passed through to the `<img>` element.
     * Providing sizes causes the image to create an automatic responsive srcset.
     *
     * We need to have approximate (rounded up) width image either in px or vw to generate srcset
     *
     * If `array` is provided then we expect per size values [S, M, L, X].
     * `string` values is consider direct size.
     * `number` is considered vw size.
     * 'auto' string means we will automatically calculate size (low performance).
     */
    @Input({ transform: sizesAttribute })
    sizes?: string | string[];

    /**
     * For responsive images: the intrinsic width of the image in pixels.
     * For fixed size images: the desired rendered width of the image in pixels.
     */
    @Input({ transform: numberAttribute })
    width: number | undefined;

    /**
     * For responsive images: the intrinsic height of the image in pixels.
     * For fixed size images: the desired rendered height of the image in pixels.
     */
    @Input({ transform: numberAttribute })
    height: number | undefined;

    @Input()
    minSize?: number;

    @Input()
    maxSize?: number;

    /**
     * The desired loading behavior (lazy, eager, or auto). Defaults to `lazy`,
     * which is recommended for most images.
     *
     * Warning: Setting images as loading="eager" or loading="auto" marks them
     * as non-priority images and can hurt loading performance. For images which
     * may be the LCP element, use the `priority` attribute instead of `loading`.
     */
    @Input()
    loading?: 'lazy' | 'eager' | 'auto';

    /**
     * Indicates whether this image should have a high priority.
     */
    @Input({ transform: booleanAttribute })
    priority = false;

    /**
     * Data to pass through to custom loaders.
     */
    @Input()
    loaderParams?: Partial<IResizeOptions>;

    /**
     * Disables automatic srcset generation for this image.
     */
    @Input({ transform: booleanAttribute })
    disableOptimizedSrcset = false;

    /**
     * Sets the image to "fill mode", which eliminates the height/width requirement and adds
     * styles such that the image fills its containing element.
     */
    @Input({ transform: booleanAttribute })
    fill = false;

    /**
     * A URL or data URL for an image to be used as a placeholder while this image loads.
     */
    @Input({ transform: booleanOrDataUrlAttribute })
    placeholder?: string | boolean;

    /**
     * Configuration object for placeholder settings. Options:
     *   * blur: Setting this to false disables the automatic CSS blur.
     */
    @Input()
    placeholderConfig?: ImagePlaceholderConfig;

    /**
     * Value of the `src` attribute if set on the host `<img>` element.
     * This input is exclusively read to assert that `src` is not set in conflict
     * with `dfImage` and that images don't start to load until a lazy loading strategy is set.
     */
    @Input()
    src?: string;

    /**
     * Value of the `srcset` attribute if set on the host `<img>` element.
     * This input is exclusively read to assert that `srcset` is not set in conflict
     * with `dfSrcset` and that images don't start to load until a lazy loading strategy is set.
     */
    @Input()
    srcset?: string;

    @Input()
    @HostBinding('attr.alt')
    alt?: string;

    @Input()
    @HostBinding('attr.title')
    title?: string;

    @Input()
    zoom: number | undefined;

    /**
     * The desired decoding behavior (sync, async, or auto). Defaults to `auto`,
     * which matches the browser's default behavior.
     *
     * - `sync`: Decode the image synchronously for atomic presentation with other content
     * - `async`: Decode the image asynchronously to reduce delay in presenting other content
     * - `auto`: Default behavior, browser chooses the best decoding method
     */
    @Input()
    decoding?: 'sync' | 'async' | 'auto';

    private imageLoader = inject(IMAGE_LOADER);
    private config: ImageConfig = processConfig(inject(IMAGE_CONFIG));
    private moduleConfig = inject(NDfModuleImage.CONFIG);
    private coreConfig = inject(NCore.OPTIONS);
    private renderer = inject(Renderer2);
    private imgElement: HTMLImageElement = inject(ElementRef).nativeElement;
    private injector = inject(Injector);
    private client = inject(Client);
    private acl = inject(Acl);
    private app = inject(App);
    private url = inject(Url);
    private destroyRef = inject(DestroyRef);

    /**
     * Calculate the rewritten `src` once and store it.
     * This is needed to avoid repetitive calculations and make sure the directive cleanup in the
     * `ngOnDestroy` does not rely on the `IMAGE_LOADER` logic (which in turn can rely on some other
     * instance that might be already destroyed).
     */
    private _renderedSrc: string | undefined = undefined;
    private _renderedSrcset: string | undefined = undefined;
    private _lastSrcsetInput: string | undefined = undefined;

    private _elementSize?: { width: number; height: number };
    private _intrinsicSize?: { width: number; height: number };
    private _sizes?: string;
    private _directSrc = false;
    private initiated = false;
    private viewportWidth = 0;

    // Cache for expensive calculations
    private _cachedRatio?: number;
    private _cachedLoadingBehavior?: string;
    private _cachedFetchPriority?: string;

    // Performance optimizations
    private rafId?: number;
    private updateScheduled = false;
    private lastElementWidth = -1;
    private lastElementHeight = -1;
    private lastViewportWidth = -1;
    private isSSR = false;
    private isHydrated = false;
    private wasSSRRendered = false;

    /**
     * Determines if placeholder background color should be applied, based on an optional boolean
     * property `placeholderBackgroundColor` within the optional configuration object `placeholderConfig` or in module config.
     */
    get shouldColorPlaceholder(): string | null {
        if (!this.placeholder) {
            return null;
        }

        if (typeof this.moduleConfig.placeholderBackgroundColor === 'string') {
            return this.moduleConfig.placeholderBackgroundColor;
        }

        return null;
    }

    ngOnInit(): void {
        // Detect SSR environment and hydration state early
        this.isSSR = !this.app.isPlatformBrowser;
        this.detectSSRRenderedState();

        // Validate decoding input
        this.validateDecodingInput();

        if (this.placeholder === undefined && this.moduleConfig.placeholder) {
            this.placeholder = this.moduleConfig.placeholder;
        }

        if (this.placeholder) {
            this.removePlaceholderOnLoad(this.imgElement);
        }

        if (this.dfImage?.alt) {
            this.alt = this.dfImage.alt;
        }

        if (this.dfImage?.title) {
            this.title = this.dfImage.title;
        }

        this.renderer.addClass(this.imgElement, 'df-image');

        // The `data-ng-img` attribute flags an image as using the directive, to allow
        // for analysis of the directive's performance.
        this.setHostAttribute('ng-img', 'true');

        // Mark as SSR-rendered for hydration detection
        if (this.isSSR) {
            this.setHostAttribute('data-ssr-rendered', 'true');
        }

        const parent = this.imgElement.parentElement;
        if (parent) {
            this.renderer.setStyle(parent, 'overflow', 'hidden');
        }

        // Initialize viewport width without causing reflow
        this.initializeViewportWidth();

        // Handle hydration vs fresh render
        if (this.wasSSRRendered && !this.isSSR) {
            this.handleHydration();
        } else {
            this.handleFreshRender();
        }

        this.initiated = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        // Invalidate caches when relevant inputs change
        if (changes['priority'] || changes['loading'] || changes['decoding']) {
            this._cachedLoadingBehavior = undefined;
            this._cachedFetchPriority = undefined;
        }

        if (changes['ratio'] || changes['width'] || changes['height']) {
            this._cachedRatio = undefined;
        }

        // Invalidate src cache when dfImage changes
        if (changes['dfImage']) {
            this._renderedSrc = undefined;
            this._renderedSrcset = undefined;
            this._lastSrcsetInput = undefined;
        }

        // During hydration, be more conservative about when to trigger updates
        if (this.isHydrated) {
            // Only force updates for critical changes during hydration
            if (changes['dfImage'] && !changes['dfImage'].isFirstChange()) {
                this.updateSrcAndSrcset(true);
            } else if (changes['priority'] || changes['loading'] || changes['decoding']) {
                // Apply essential attributes only for loading behavior changes
                this.applyEssentialAttributes();
            }
        } else {
            // Normal update behavior for fresh renders
            if (
                (changes['dfImage'] && !changes['dfImage'].isFirstChange()) ||
                (changes['ratio'] && !changes['ratio'].isFirstChange()) ||
                changes['width'] ||
                changes['height'] ||
                changes['zoom'] ||
                changes['dfSrcset']
            ) {
                this.updateSrcAndSrcset(true);
            }
        }
    }

    ngOnDestroy(): void {
        // Cancel any pending RAF to prevent memory leaks
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = undefined;
        }

        // Clear all caches to prevent memory leaks
        this._renderedSrc = undefined;
        this._renderedSrcset = undefined;
        this._lastSrcsetInput = undefined;
        this._cachedRatio = undefined;
        this._cachedLoadingBehavior = undefined;
        this._cachedFetchPriority = undefined;
        this._elementSize = undefined;
        this._intrinsicSize = undefined;
        this._sizes = undefined;
        this.updateScheduled = false;
        this.isHydrated = false;
        this.wasSSRRendered = false;
    }

    private validateDecodingInput(): void {
        const validInputs = ['sync', 'async', 'auto'];
        if (typeof this.decoding === 'string' && !validInputs.includes(this.decoding)) {
            throw new Error(
                `The DfImageDirective (activated on an <img> element with the \`dfImage="${this.dfImage?.url || 'unknown'}"\`) has detected ` +
                `that the \`decoding\` attribute has an invalid value (\`${this.decoding}\`). ` +
                'To fix this, provide a valid value ("sync", "async", or "auto").'
            );
        }
    }

    protected handleError(): void {
        if (this.client.mode === 'browser') {
            const width = Math.round(this.width || this._elementSize?.width || this._intrinsicSize?.width || 500);
            const height = Math.round(this.height || this._elementSize?.height || this._intrinsicSize?.height || 500);

            this.removeHostAttribute('srcset');

            if (this.acl.isDeveloper || !this.acl.isProduction) {
                this.setHostAttribute('src', `https://place-hold.it/${width}x${height}/f7f4f1/e12954?text=Error loading&fontsize=36px`);
            } else if (this.moduleConfig.placeholderError) {
                this.setHostAttribute('src', this.url.toMediaUrl(this.moduleConfig.placeholderError));
            }
        }
    }

    protected handleLoad(): void {
        // Update intrinsic size when image loads
        if (this.imgElement.naturalWidth && this.imgElement.naturalHeight) {
            const newIntrinsicSize = {
                width: this.imgElement.naturalWidth,
                height: this.imgElement.naturalHeight
            };

            // Only update if intrinsic size actually changed
            if (
                !this._intrinsicSize ||
                this._intrinsicSize.width !== newIntrinsicSize.width ||
                this._intrinsicSize.height !== newIntrinsicSize.height
            ) {
                this._intrinsicSize = newIntrinsicSize;

                // If ratio is auto, recalculate based on intrinsic size
                // But only if we haven't already calculated it and we're not in hydration
                if (this.ratio === 'auto' && this._intrinsicSize && !this._cachedRatio && !this.isHydrated) {
                    this.updateSrcAndSrcset(true);
                }
            }
        }
    }

    private initializeViewportWidth(): void {
        // Initialize viewport width safely for both SSR and browser
        if (this.isSSR) {
            // Use default desktop width for SSR to ensure consistent rendering
            this.viewportWidth = this.client.width || 1200;
        } else if (typeof window !== 'undefined') {
            // Use cached window.innerWidth to avoid reflow
            this.viewportWidth = window.innerWidth;
        } else {
            this.viewportWidth = this.client.width || 1200;
        }
        this.lastViewportWidth = this.viewportWidth;
    }

    private updateViewportWidth(): void {
        // Only update if we're in browser and value actually changed
        if (!this.isSSR && typeof window !== 'undefined') {
            const newWidth = window.innerWidth;
            if (Math.abs(newWidth - this.lastViewportWidth) > 10) {
                // 10px threshold to avoid micro-updates
                this.viewportWidth = newWidth;
                this.lastViewportWidth = newWidth;
            }
        }
    }

    private scheduleUpdate(updateFn: () => void): void {
        if (this.isSSR) {
            // Execute immediately in SSR
            updateFn();
            return;
        }

        if (!this.updateScheduled) {
            this.updateScheduled = true;
            this.rafId = requestAnimationFrame(() => {
                updateFn();
                this.updateScheduled = false;
                this.rafId = undefined;
            });
        }
    }

    private detectSSRRenderedState(): void {
        // Check if this image was already rendered by SSR
        if (!this.isSSR) {
            // In browser, check for SSR markers
            this.wasSSRRendered =
                this.imgElement.hasAttribute('data-ssr-rendered') ||
                this.imgElement.hasAttribute('src') ||
                this.imgElement.hasAttribute('srcset');
        }
    }

    private handleHydration(): void {
        // During hydration, preserve existing attributes and only setup observers
        this.isHydrated = true;

        // Cache existing dimensions if available
        if (this.imgElement.naturalWidth && this.imgElement.naturalHeight) {
            this._intrinsicSize = {
                width: this.imgElement.naturalWidth,
                height: this.imgElement.naturalHeight
            };
        }

        // Cache existing src/srcset to avoid regeneration
        const existingSrc = this.imgElement.getAttribute('src');
        const existingSrcset = this.imgElement.getAttribute('srcset');

        if (existingSrc) {
            this._renderedSrc = existingSrc;
        }

        if (existingSrcset) {
            this._renderedSrcset = existingSrcset;
            // Mark that we have a valid srcset from SSR to prevent regeneration
            this._lastSrcsetInput = this.dfSrcset || 'ssr-generated';
        }

        // Remove SSR marker
        this.removeHostAttribute('data-ssr-rendered');

        // Only apply essential attributes that don't affect src/srcset during hydration
        this.applyEssentialAttributes();

        // Setup observers for future updates only
        this.scheduleUpdate(() => {
            this.setupResizeObserver();
            this.setupBreakpointObserver();
            this.setupViewportResizeObserver();
        });
    }

    private applyEssentialAttributes(): void {
        // During hydration, only apply essential attributes that don't affect src/srcset
        // to avoid unnecessary regeneration of already SSR-rendered content

        const updates: Array<{ type: 'set' | 'remove'; attr: string; value?: string }> = [];

        // Apply loading behavior attributes
        updates.push({ type: 'set', attr: 'loading', value: this.getLoadingBehavior() });
        updates.push({ type: 'set', attr: 'fetchpriority', value: this.getFetchPriority() });
        updates.push({ type: 'set', attr: 'decoding', value: this.getDecodingBehavior() });

        // Apply dimension attributes if needed
        this.collectDimensionUpdates(updates);

        // Apply sizes if needed (but don't regenerate srcset)
        this.collectSizesUpdates(updates);

        // Apply all updates immediately
        this.applyDomUpdates(updates);
    }

    private handleFreshRender(): void {
        // Fresh render - setup everything normally

        // Only setup observers in browser environment
        if (!this.isSSR) {
            // Defer observer setup to next frame to avoid blocking initial render
            this.scheduleUpdate(() => {
                this.setupResizeObserver();
                this.setupBreakpointObserver();
                this.setupViewportResizeObserver();
            });
        }

        // The `src` and `srcset` attributes should be set last since other attributes
        // could affect the image's loading behavior.
        const rewrittenSrcset = this.updateSrcAndSrcset();

        // Preload the image if it's a priority image.
        if (this.priority && this.isSSR) {
            const preloadLinkCreator = this.injector.get(PreloadLinkCreator);
            const sizes = this.getSizes(false);

            preloadLinkCreator.createPreloadLinkTag(this.renderer, this.getRewrittenSrc(), rewrittenSrcset, sizes);
        }
    }

    private setupResizeObserver(): void {
        // Only setup if we actually need responsive behavior
        if (!this.shouldSetupResizeObserver()) {
            return;
        }

        resizeObservable(
            this.imgElement,
            (event: ResizeObserverEntry) => {
                const newWidth = Math.round(event.contentRect.width);
                const newHeight = Math.round(event.contentRect.height);

                // Only update if size actually changed significantly (avoid micro-updates)
                // Use larger threshold to prevent excessive updates
                if (Math.abs(this.lastElementWidth - newWidth) > 5 || Math.abs(this.lastElementHeight - newHeight) > 5) {
                    this.lastElementWidth = newWidth;
                    this.lastElementHeight = newHeight;
                    this._elementSize = { width: newWidth, height: newHeight };

                    // Schedule batched update to prevent layout thrashing
                    this.scheduleUpdate(() => {
                        this.updateSrcAndSrcset(true);
                    });
                }
            },
            0
        )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    private shouldSetupResizeObserver(): boolean {
        // Only setup if we have responsive behavior that actually needs it
        return !!(this.sizes || this.ratio === 'auto' || this.fill || this.dfSrcset);
    }

    private setupBreakpointObserver(): void {
        // Only setup if we have responsive sizes or dynamic ratio
        if (!this.shouldSetupBreakpointObserver()) {
            return;
        }

        this.client.obs.breakpoint
            .pipe(debounceTime(RESIZE_DEBOUNCE_TIME), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                // Schedule batched update to prevent layout thrashing
                this.scheduleUpdate(() => {
                    this.updateSrcAndSrcset(true);
                });
            });
    }

    private shouldSetupBreakpointObserver(): boolean {
        // Only setup if we have responsive sizes or dynamic ratio behavior
        return !!(Array.isArray(this.sizes) || (this.ratio && typeof this.ratio !== 'number'));
    }

    private setupViewportResizeObserver(): void {
        // Only setup if we need viewport-based calculations
        if (!this.shouldSetupViewportResizeObserver()) {
            return;
        }

        this.client.obs.resize
            .pipe(debounceTime(RESIZE_DEBOUNCE_TIME), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.updateViewportWidth();
                // Schedule batched update to prevent layout thrashing
                this.scheduleUpdate(() => {
                    this.updateSrcAndSrcset(true);
                });
            });
    }

    private shouldSetupViewportResizeObserver(): boolean {
        // Only setup if we have viewport-dependent behavior
        return this.client.mode === 'browser' && !!(this.sizes || this.ratio === 'auto' || this.fill);
    }

    private updateSrcAndSrcset(forceSrcRecalc = false): string | undefined {
        // Skip updates during hydration if image is already properly rendered
        if (this.isHydrated && !forceSrcRecalc && this._renderedSrc && this._renderedSrcset) {
            return this._renderedSrcset;
        }

        // During hydration, avoid regenerating srcset unless explicitly forced
        if (this.isHydrated && !forceSrcRecalc) {
            // Only apply essential attributes during hydration, skip src/srcset updates
            this.applyEssentialAttributes();
            return this._renderedSrcset;
        }

        // Only reset cache if we actually need to recalculate
        if (forceSrcRecalc && this._renderedSrc) {
            // Reset cached value, so that the followup `getRewrittenSrc()` call
            // will recalculate it and update the cache.
            this._renderedSrc = undefined;
        }

        // Collect all DOM updates to batch them efficiently
        const updates: Array<{ type: 'set' | 'remove'; attr: string; value?: string }> = [];

        // Collect dimension updates
        this.collectDimensionUpdates(updates);

        // Collect loading behavior updates
        updates.push({ type: 'set', attr: 'loading', value: this.getLoadingBehavior() });
        updates.push({ type: 'set', attr: 'fetchpriority', value: this.getFetchPriority() });
        updates.push({ type: 'set', attr: 'decoding', value: this.getDecodingBehavior() });

        // Collect sizes updates
        this.collectSizesUpdates(updates);

        // Collect src updates
        let rewrittenSrc = this.app.isPlatformBrowser ? this.getRewrittenSrc() : undefined;
        if (rewrittenSrc) {
            updates.push({ type: 'set', attr: 'src', value: rewrittenSrc });
        } else {
            updates.push({ type: 'remove', attr: 'src' });
        }

        // Collect srcset updates
        rewrittenSrc = this.collectSrcsetUpdates(updates, rewrittenSrc);

        // Apply all updates immediately during initialization or SSR, batch for subsequent browser updates
        if (!this.initiated || this.isSSR) {
            this.applyDomUpdates(updates);
        } else {
            this.scheduleUpdate(() => {
                this.applyDomUpdates(updates);
            });
        }

        return rewrittenSrc;
    }

    private collectDimensionUpdates(updates: Array<{ type: 'set' | 'remove'; attr: string; value?: string }>): void {
        const width = this.width;
        if (width !== undefined) {
            updates.push({ type: 'set', attr: 'width', value: width.toString() });

            const height = this.height;
            const ratio = this.getRatio();

            if (height !== undefined) {
                updates.push({ type: 'set', attr: 'height', value: height.toString() });
            } else if (width && ratio && typeof ratio === 'number') {
                updates.push({ type: 'set', attr: 'height', value: (width / ratio).toString() });
            }
        }
    }

    private collectSizesUpdates(updates: Array<{ type: 'set' | 'remove'; attr: string; value?: string }>): void {
        const sizes = this.getSizes();
        if (sizes && this._sizes !== sizes) {
            this._sizes = sizes;
            if (sizes) {
                if (this.getLoadingBehavior() === 'lazy') {
                    updates.push({ type: 'set', attr: 'sizes', value: 'auto, ' + sizes });
                } else {
                    updates.push({ type: 'set', attr: 'sizes', value: sizes });
                }
            } else {
                updates.push({ type: 'remove', attr: 'sizes' });
            }
        } else if (!sizes) {
            this._sizes = sizes;
            updates.push({ type: 'remove', attr: 'sizes' });
        }
    }

    private collectSrcsetUpdates(
        updates: Array<{ type: 'set' | 'remove'; attr: string; value?: string }>,
        rewrittenSrcset?: string
    ): string | undefined {
        // Skip srcset updates during hydration unless explicitly forced
        if (this.isHydrated && !this._renderedSrcset) {
            return rewrittenSrcset;
        }

        if (!this._directSrc && this.dfImage?.url) {
            if (this.dfSrcset) {
                rewrittenSrcset = this.getRewrittenSrcset();
            } else if (this.shouldGenerateAutomaticSrcset()) {
                rewrittenSrcset = this.getAutomaticSrcset();
            }

            if (rewrittenSrcset) {
                updates.push({ type: 'set', attr: 'srcset', value: rewrittenSrcset });
            } else {
                updates.push({ type: 'remove', attr: 'srcset' });
            }
        }

        return rewrittenSrcset;
    }

    private applyDomUpdates(updates: Array<{ type: 'set' | 'remove'; attr: string; value?: string }>): void {
        updates.forEach(update => {
            if (update.type === 'set') {
                this.setHostAttribute(update.attr, update.value!);
            } else {
                this.removeHostAttribute(update.attr);
            }
        });
    }

    private callImageLoader(configWithoutCustomParams: Omit<ImageLoaderConfig, 'loaderParams'>): string | undefined {
        const ratio = this.getRatio();
        const augmentedConfig: ImageLoaderConfig = configWithoutCustomParams;

        if (this.loaderParams) {
            augmentedConfig.loaderParams = this.loaderParams;
        }

        if (this.loaderParams?.height === undefined && augmentedConfig.width && ratio) {
            augmentedConfig.loaderParams = Object.assign({}, augmentedConfig.loaderParams, {
                height: Math.round(augmentedConfig.width / ratio)
            });
        }

        try {
            return this.imageLoader(augmentedConfig);
        } catch (error) {
            console.error('error', error);
            return undefined;
        }
    }

    private getRatio(): number | undefined {
        // Return cached ratio if available and inputs haven't changed
        if (this._cachedRatio !== undefined && this.ratio !== 'auto' && !this._intrinsicSize && !this._elementSize) {
            return this._cachedRatio;
        }

        let ratio = this.ratio;

        if (ratio && ratio === 'auto') {
            // Try to get ratio from intrinsic size first, then element size
            if (this._intrinsicSize) {
                ratio = this._intrinsicSize.width / this._intrinsicSize.height;
            } else if (this._elementSize) {
                ratio = this._elementSize.width / this._elementSize.height;
            }
        }

        if (ratio) {
            this._cachedRatio = Number(ratio);
            return this._cachedRatio;
        }

        return undefined;
    }

    private getLoadingBehavior(): string {
        // Cache the result since it's called frequently
        if (this._cachedLoadingBehavior !== undefined) {
            return this._cachedLoadingBehavior;
        }

        const behavior = !this.priority && this.loading !== undefined ? this.loading : this.priority ? 'eager' : 'lazy';

        this._cachedLoadingBehavior = behavior;
        return behavior;
    }

    private getFetchPriority(): string {
        // Cache the result since it's called frequently
        if (this._cachedFetchPriority !== undefined) {
            return this._cachedFetchPriority;
        }

        const priority = this.priority ? 'high' : 'auto';
        this._cachedFetchPriority = priority;
        return priority;
    }

    private getDecodingBehavior(): string {
        // Default to 'auto' if not specified, but use 'sync' for priority images
        if (this.decoding !== undefined) {
            return this.decoding;
        }

        return this.priority ? 'sync' : 'auto';
    }

    private getRewrittenSrc(): string | undefined {
        if (this.dfImage === undefined && (this.acl.isDeveloper || !this.acl.isProduction)) {
            const width = Math.round(this.width || this._elementSize?.width || this._intrinsicSize?.width || 500);
            const height = Math.round(this.height || this._elementSize?.height || this._intrinsicSize?.height || 500);

            return `https://place-hold.it/${width}x${height}/f7f4f1/e12954?text=Invalid image source&fontsize=36px`;
        }

        // Return cached value if available
        if (this._renderedSrc) {
            return this._renderedSrc;
        }

        // ImageLoaderConfig supports setting a width property. However, we're not setting width here
        // because if the developer uses rendered width instead of intrinsic width in the HTML width
        // attribute, the image requested may be too small for 2x+ screens.
        const src = this.dfImage?.url;

        this._directSrc = !!(src && (src.match(/\.(svg|gif)$/) || src.match(/^https?:\/\//)));

        // Cache calculated image src to reuse it later in the code.
        this._renderedSrc = this.url.toMediaUrl(src);

        return this._renderedSrc;
    }

    private getRewrittenSrcset(): string {
        if (!this.dfSrcset) {
            return '';
        }

        // Check if we can use cached result
        if (this._renderedSrcset && this._lastSrcsetInput === this.dfSrcset) {
            return this._renderedSrcset;
        }

        const widthSrcSet = VALID_WIDTH_DESCRIPTOR_SRCSET.test(this.dfSrcset);
        const _width = Math.round(this.width || this._elementSize?.width || this._intrinsicSize?.width || 500);

        const finalSrcs = this.dfSrcset
            .split(',')
            .filter(src => src !== '')
            .map(srcStr => {
                srcStr = srcStr.trim();
                const width = widthSrcSet ? parseFloat(srcStr) : parseFloat(srcStr) * _width;

                const loadingWidth = Math.round(width * (this.zoom || 1));

                return `${this.callImageLoader({ src: this.dfImage?.url, width: loadingWidth })} ${srcStr}`;
            });

        const result = finalSrcs.join(', ');

        // Cache the result
        this._renderedSrcset = result;
        this._lastSrcsetInput = this.dfSrcset;

        return result;
    }

    private getAutomaticSrcset(): string {
        if (this._sizes) {
            return this.getResponsiveSrcset();
        }

        return this.getFixedSrcset();
    }

    private getResponsiveSrcset(): string {
        const { breakpoints } = this.config;
        let filteredBreakpoints = breakpoints;

        if (this._sizes?.trim() === '100vw' && breakpoints) {
            // Since this is a full-screen-width image, our srcset only needs to include
            // breakpoints with full viewport widths.
            filteredBreakpoints = breakpoints.filter(bp => bp >= VIEWPORT_BREAKPOINT_CUTOFF);
        }

        const minSize = this.minSize;

        if (minSize && filteredBreakpoints) {
            filteredBreakpoints = filteredBreakpoints.filter(bp => bp >= minSize);
        }

        const maxSize = this.maxSize;

        if (maxSize && filteredBreakpoints) {
            filteredBreakpoints = filteredBreakpoints.filter(bp => bp <= maxSize);
        }

        if (filteredBreakpoints) {
            const finalSrcs = filteredBreakpoints.map(bp => {
                const image = `${this.callImageLoader({ src: this.dfImage.url, width: bp })}`.replace(/ /g, '%20');

                return `${image} ${bp}w`;
            });

            return finalSrcs.join(', ');
        }

        return '';
    }

    private getFixedSrcset(): string {
        const width = Math.round(this.width || this._elementSize?.width || this._intrinsicSize?.width || 500);

        const finalSrcs = DENSITY_SRCSET_MULTIPLIERS.map(multiplier => {
            // Apply zoom to loading width for higher resolution
            const loadingWidth = Math.round(width * (this.zoom || 1) * multiplier);

            return `${this.callImageLoader({
                src: this.dfImage.url,
                width: loadingWidth
            })} ${multiplier}x`;
        });

        return finalSrcs.join(', ');
    }

    private getSizes(auto = true): string | undefined {
        if (this._directSrc) {
            return undefined;
        }

        if ((this.fill || this.ratio) && !this.sizes && auto) {
            return this.getAutoSizes();
        }

        if (this.sizes) {
            if (Array.isArray(this.sizes)) {
                const mediaQueries: string[] = [];

                this.sizes.forEach((size, index) => {
                    switch (index) {
                        case 0:
                            mediaQueries.push(`(max-width: ${this.coreConfig.breakpoints.M.width - 1}px) ${size}`);
                            break;
                        case 1:
                            mediaQueries.push(
                                `((min-width: ${this.coreConfig.breakpoints.M.width}px) and (max-width: ${this.coreConfig.breakpoints.L.width - 1}px)) ${size}`
                            );
                            break;
                        case 2:
                            mediaQueries.push(
                                `((min-width: ${this.coreConfig.breakpoints.L.width}px) and (max-width: ${this.coreConfig.breakpoints.X.width - 1}px)) ${size}`
                            );
                            break;
                        case 3:
                            mediaQueries.push(`(min-width: ${this.coreConfig.breakpoints.X.width}px) ${size}`);
                            break;
                    }
                });

                return mediaQueries.join(', ');
            }

            return this.sizes;
        }

        return undefined;
    }

    private getAutoSizes() {
        // Optimized size detection with cached values - NO REFLOW
        let elementWidth: number;

        // Priority order for width detection (all reflow-free):
        // 1. Explicit width input
        // 2. Element size from ResizeObserver (cached)
        // 3. Intrinsic size from naturalWidth (cached)
        // 4. Viewport width (cached, no DOM query)

        if (this.width) {
            elementWidth = this.width;
        } else if (this._elementSize?.width) {
            elementWidth = this._elementSize.width;
        } else if (this._intrinsicSize?.width) {
            elementWidth = this._intrinsicSize.width;
        } else {
            // Use cached viewport width to avoid reflow
            elementWidth = this.viewportWidth || 1200; // Fallback for SSR
        }

        // Ensure we have a valid viewport width
        const safeViewportWidth = this.viewportWidth || 1200;

        // If we have a specific element width that's significantly different from viewport width, use pixels
        if (elementWidth && Math.abs(elementWidth - safeViewportWidth) > 50) {
            return `${Math.ceil(elementWidth)}px`;
        }

        // Otherwise calculate viewport percentage using cached viewport width
        const vwPercentage = Math.max(10, Math.min(100, Math.round((elementWidth / safeViewportWidth) * 100)));
        return `${vwPercentage}vw`;
    }

    private shouldGenerateAutomaticSrcset(): boolean {
        let oversizedImage = false;

        if (!this._sizes) {
            const ratio = this.getRatio();
            const width = Math.round(this.width || this._elementSize?.width || this._intrinsicSize?.width || 500);

            if (!this.height && ratio && width) {
                this.height = Math.round(width / ratio);
            }

            oversizedImage = width > FIXED_SRCSET_WIDTH_LIMIT || (this.height ? this.height > FIXED_SRCSET_HEIGHT_LIMIT : false);
        }

        return !this.disableOptimizedSrcset && !this.srcset && this.imageLoader !== noopImageLoader && !oversizedImage;
    }

    /**
     * Returns an image url formatted for use with the CSS background-image property. Expects one of:
     * - A base64 encoded image, which is wrapped and passed through.
     * - A boolean. If true, calls the image loader to generate a small placeholder url.
     */
    private generatePlaceholder(placeholderInput: string | boolean): string | boolean | null {
        const { placeholderResolution } = this.config;

        if (placeholderInput === true && this.dfImage?.url) {
            return `url(${this.callImageLoader({
                src: this.dfImage.url,
                width: placeholderResolution,
                isPlaceholder: true
            })})`;
        } else if (typeof placeholderInput === 'string' && placeholderInput.startsWith('data:')) {
            return `url("${placeholderInput}")`;
        }

        return null;
    }

    /**
     * Determines if blur should be applied, based on an optional boolean
     * property `blur` within the optional configuration object `placeholderConfig` or in module config.
     */
    private shouldBlurPlaceholder(placeholderConfig?: ImagePlaceholderConfig): string | null {
        if (!this.placeholder) {
            return null;
        }

        if (this.dfImage === undefined) {
            if (this.acl.isDeveloper || !this.acl.isProduction) {
                return null;
            }

            return 'opacity(0%)';
        }

        if (
            (placeholderConfig && (!Object.prototype.hasOwnProperty.call(placeholderConfig, 'blur') || Boolean(placeholderConfig.blur))) ||
            (typeof this.moduleConfig.placeholderBlur === 'boolean' && this.moduleConfig.placeholderBlur)
        ) {
            return `blur(${PLACEHOLDER_BLUR_AMOUNT}px)`;
        } else if (typeof this.moduleConfig.placeholderBlur === 'number') {
            return `blur(${this.moduleConfig.placeholderBlur}px)`;
        }

        return null;
    }

    private removePlaceholderOnLoad(img: HTMLImageElement): void {
        const callback = () => {
            const changeDetectorRef = this.injector.get(ChangeDetectorRef);
            removeLoadListenerFn();
            removeErrorListenerFn();

            if (this.dfImage?.url) {
                this.placeholder = false;
            }

            // TODO: Change to `markForCheck` when new zone is available (Angular 18+)
            changeDetectorRef.detectChanges();
        };

        const removeLoadListenerFn = this.renderer.listen(img, 'load', callback);
        const removeErrorListenerFn = this.renderer.listen(img, 'error', callback);
    }

    private setHostAttribute(name: string, value: string): void {
        this.renderer.setAttribute(this.imgElement, name, value);
    }

    private removeHostAttribute(name: string): void {
        this.renderer.removeAttribute(this.imgElement, name);
    }

    protected handleMouseEnter(): void {
        if (this.zoom) {
            this.renderer.setStyle(this.imgElement, 'scale', this.zoom);
        }
    }

    protected handleMouseLeave(): void {
        if (this.zoom) {
            this.renderer.setStyle(this.imgElement, 'scale', 1);
        }
    }
}

/***** Helpers *****/

/**
 * Sorts provided config breakpoints and uses defaults.
 */
function processConfig(config: ImageConfig): ImageConfig {
    const sortedBreakpoints: { breakpoints?: number[] } = {};

    if (config.breakpoints) {
        sortedBreakpoints.breakpoints = config.breakpoints.sort((a, b) => a - b);
    }

    return Object.assign({}, IMAGE_CONFIG_DEFAULTS, config, sortedBreakpoints);
}

// Transform function to handle inputs which may be booleans, strings, or string representations
// of boolean values. Used for the placeholder attribute.
export function booleanOrDataUrlAttribute(value: boolean | string): boolean | string {
    if (typeof value === 'string' && value.startsWith('data:')) {
        return value;
    }

    return booleanAttribute(value);
}

// Transform function to handle inputs which may be number, strings, or string representations
// of number values. Used for the ratio attribute.
export function ratioAttribute(value: number | string): number | 'auto' {
    let ratio: number;

    if (typeof value === 'string') {
        if (value === 'auto') {
            return value;
        }

        const match = value.match(/(ratio-)?(\d+)-(\d+)/);

        if (match?.length === 4) {
            ratio = parseFloat(match[2]) / parseFloat(match[3]);
        } else if (value.includes('/')) {
            // `ratio` attribute can be provided as a fraction, e.g. "3/4".
            const parts = value.split('/');

            if (parts.length === 2) {
                ratio = parseFloat(parts[0]) / parseFloat(parts[1]);
            } else {
                ratio = parseFloat(value);
            }
        } else if (value.includes('-')) {
            // `ratio` attribute can be provided as a fraction, e.g. "3-4".
            const parts = value.split('-');

            if (parts.length === 2) {
                ratio = parseFloat(parts[0]) / parseFloat(parts[1]);
            } else {
                ratio = parseFloat(value);
            }
        } else {
            ratio = parseFloat(value);
        }
    } else {
        ratio = 1 / value; // Invert the ratio to get the width/height ratio
    }

    return Math.round(ratio * 100) / 100;
}

// Transform function to handle inputs which may be string, sNDfModuleImage.IImage, or NDfModuleImage.ToImage.
// Used for the dfImage attribute.
export function imageAttribute(value: string | NDfModuleImage.IImage | NDfModuleImage.ToImage): NDfModuleImage.IImage | undefined {
    if (typeof value === 'string' && value.trim() !== '') {
        if (value.match(/^https?:\/\//)) {
            return { url: value };
        } else {
            const parts = value.split(/:|&&/);

            return {
                url: parts[0],
                alt: parts[1],
                title: parts[2]
            };
        }
    }

    if (value && typeof value === 'object') {
        if ((value as NDfModuleImage.ToImage).toImage) {
            return (value as NDfModuleImage.ToImage).toImage();
        }

        if ((value as NDfModuleImage.IImage).url) {
            return value as NDfModuleImage.IImage;
        }
    }

    return undefined;
}

// Transform function to handle inputs which may be number, strings, or string array.
// Used for the sizes attribute.
export function sizesAttribute(value: number | string | number[] | string[]): string[] | string {
    if (Array.isArray(value)) {
        return value.map((v: string | number) => {
            if (typeof v === 'number') {
                return v + 'vw';
            }

            return v === 'auto' ? '100vw' : v;
        });
    }

    if (typeof value === 'number') {
        return value + 'vw';
    }

    return value;
}
